import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { MessageWithSupportTicketId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { BasicIndex } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { getSupportTicketIdScope } from "./support-ticket-collection-scope";
import {
  supportTicketQueryScopeKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";
import { parseCollectionFilters } from "./support-ticket-tanstack-scope";
import {
  fetchSupportTicketMessages,
  fetchSupportTicketThreads,
  type SupportTicketThreadCollectionItem,
  type SupportTicketTranscriptApi,
} from "./support-ticket-transcript-requests";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const supportTicketTranscriptQueryKeys = {
  all: ["supportTickets", "transcript"] as const,
  threads: () => ["supportTickets", "transcript", "threads"] as const,
  threadsCollection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketTranscriptQueryKeys.threads(),
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  messages: () => ["supportTickets", "transcript", "messages"] as const,
  messagesCollection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketTranscriptQueryKeys.messages(),
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  timelineEvents: () =>
    ["supportTickets", "transcript", "timelineEvents"] as const,
  timelineEventsCollection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketTranscriptQueryKeys.timelineEvents(),
      ...supportTicketQueryScopeKey(scope),
    ] as const,
};

export interface CreateSupportTicketTranscriptCollectionsOptions {
  queryClient: QueryClient;
  api: SupportTicketTranscriptApi;
  scope?: SupportTicketQueryScope;
}

function getSupportTicketIdFromLoadSubsetOptions(
  loadSubsetOptions: unknown,
): SupportTicketId | null {
  return getSupportTicketIdScope(parseCollectionFilters(loadSubsetOptions));
}

export function createThreadsCollection({
  queryClient,
  api,
  scope,
}: CreateSupportTicketTranscriptCollectionsOptions) {
  return createCollection<SupportTicketThreadCollectionItem, string>(
    queryCollectionOptions({
      queryKey: supportTicketTranscriptQueryKeys.threadsCollection(scope),
      queryFn: async (ctx): Promise<SupportTicketThreadCollectionItem[]> => {
        const supportTicketId = getSupportTicketIdFromLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        return supportTicketId
          ? fetchSupportTicketThreads(api, supportTicketId)
          : [];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
    }),
  );
}

export function createMessagesCollection({
  queryClient,
  api,
  scope,
}: CreateSupportTicketTranscriptCollectionsOptions) {
  return createCollection<MessageWithSupportTicketId, string>(
    queryCollectionOptions({
      queryKey: supportTicketTranscriptQueryKeys.messagesCollection(scope),
      queryFn: async (ctx): Promise<MessageWithSupportTicketId[]> => {
        const supportTicketId = getSupportTicketIdFromLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        return supportTicketId
          ? fetchSupportTicketMessages(api, supportTicketId)
          : [];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
    }),
  );
}

export function createTimelineEventsCollection({
  queryClient,
  api,
  scope,
}: CreateSupportTicketTranscriptCollectionsOptions) {
  return createCollection<SupportTicketTimelineEventResponse, string>(
    queryCollectionOptions({
      queryKey:
        supportTicketTranscriptQueryKeys.timelineEventsCollection(scope),
      queryFn: async (ctx): Promise<SupportTicketTimelineEventResponse[]> => {
        const supportTicketId = getSupportTicketIdFromLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        if (!supportTicketId) {
          return [];
        }

        const result = await api.getTimelineEvents(supportTicketId);
        return [...result.items];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
    }),
  );
}

export type ThreadsCollection = ReturnType<typeof createThreadsCollection>;
export type MessagesCollection = ReturnType<typeof createMessagesCollection>;
export type TimelineEventsCollection = ReturnType<
  typeof createTimelineEventsCollection
>;

export {
  fetchSupportTicketMessages,
  fetchSupportTicketThreads,
  type SupportTicketThreadCollectionItem,
  type SupportTicketTranscriptApi,
} from "./support-ticket-transcript-requests";
